import React, { useEffect, useLayoutEffect, useState } from 'react'

import { useLocation } from 'react-router-dom'

import { useAsyncEffect } from '@hooks/index'
import http from '@utils/http'

export type InfiniteFetchOptions<T, S = HTMLElement, L = HTMLElement> = {
  getURL: (page: number) => string
  responseParser: (resp: any) => { data: T[]; hasMore: boolean }
  transformer?: (data: any) => T
  initialData?: T[]
  initialRemaining?: boolean
  distance?: number
  scrollRef?: React.RefObject<S>
  loadingRef?: React.RefObject<L>

  /** Whether or not to make the initial fetch (page 0) */
  makeInitialFetch?: boolean
}

export default function useInfiniteFetch<T, S = HTMLElement, L = HTMLElement>({
  getURL,
  responseParser,
  initialData,
  initialRemaining,
  distance = 250,
  scrollRef,
  loadingRef,
  makeInitialFetch,
  transformer = defaultTransformer,
}: InfiniteFetchOptions<T, S, L>) {
  const { search } = useLocation()

  const [results, setResults] = useState<T[]>(initialData ?? [])
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(0)

  const [scrollNode, setScrollNode] = useState<S | null>(null)
  const [loaderNode, setLoaderNode] = useState<L | null>(null)

  /** Use passed in loading ref when available */
  useEffect(() => {
    setLoaderNode(loadingRef?.current ?? null)
  }, [loadingRef])

  /** Use passed in scroll container ref when available */
  useEffect(() => {
    setScrollNode(scrollRef?.current ?? null)
  }, [scrollRef])

  /**
   * Reset infinite scroll if search changes
   */
  useEffect(() => {
    setPage(0)
    setHasMore(false)
  }, [search])

  /**
   * If the initial data changes, reset paging to 0
   */
  useEffect(() => {
    setResults(initialData ?? [])
    setPage(0)
    setHasMore(initialRemaining ?? false)
  }, [initialData, initialRemaining])

  /**
   * When the "page" changes, load more titles
   */
  useAsyncEffect(
    async (isActive) => {
      if (page === 0 && (initialData?.length || !makeInitialFetch)) return

      const [resp] = await http.get(getURL(page))

      // Don't set state if component is no longer mounted
      if (!isActive()) return

      if (resp?.data) {
        const { data, hasMore } = responseParser(resp.data)
        setResults((prevResults) => [...prevResults, ...data.map(transformer)])
        setHasMore(hasMore)
      } else {
        setHasMore(false)
      }
    },
    [page]
  )

  /**
   * Handles when to change pages using intersection with loading indicator
   */
  useLayoutEffect(() => {
    if (!scrollNode || !loaderNode || !hasMore) return

    const options: IntersectionObserverInit = {
      root: scrollNode as never,
      rootMargin: `0px 0px ${distance}px 0px`,
    }

    let previousY
    let previousRatio = 0

    const listener: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        const { y } = entry.boundingClientRect
        if (
          entry.isIntersecting &&
          entry.intersectionRatio >= previousRatio &&
          (!previousY || y < previousY)
        ) {
          setPage((page) => page + 1)
        }
        previousY = y
        previousRatio = entry.intersectionRatio
      })
    }

    const observer = new IntersectionObserver(listener, options)
    observer.observe(loaderNode as never)

    return () => observer.disconnect()
  }, [hasMore, distance, loaderNode, scrollNode])

  return { page, setLoaderNode, setScrollNode, results, hasMore }
}

export const defaultPaginationURL = (resultsPerPage: number) => (page: number) => {
  const { searchParams } = new URL(window.location.href)
  searchParams.set('page', `${page}`)
  searchParams.set('max_count', `${resultsPerPage}`)
  return `${window.location.pathname}?${searchParams.toString()}`
}

export const defaultResponseParser = (key: string) => (resp: any) => {
  if (Array.isArray(resp[key])) {
    return {
      data: resp[key],
      hasMore: resp[key].length !== 0 && resp.remaining > 0,
    }
  }
  return { data: [], hasMore: false }
}

export const defaultTransformer = <T>(data: any): T => {
  return data
}
